/**
 * SettingsPage — per-Holder defaults: autonomy level + monthly spend cap.
 * Channel connections + wallet management embed here in Phase 4.
 */

import { useEffect, useState } from 'react';
import { useAdoptSettings, usePutAdoptSettings } from '../hooks/useAdvertisingOptimization';
import type { AdoptAutonomyLevelDto } from '@/lib/api';

interface Props {
  holderId: string;
}

export function SettingsPage({ holderId }: Props) {
  const settingsQuery = useAdoptSettings(holderId);
  const putSettings = usePutAdoptSettings(holderId);

  const [autonomy, setAutonomy] = useState<AdoptAutonomyLevelDto>('propose');
  const [monthlyCap, setMonthlyCap] = useState<string>('');

  useEffect(() => {
    if (settingsQuery.data) {
      setAutonomy(settingsQuery.data.defaultAutonomyLevel);
      setMonthlyCap(
        settingsQuery.data.monthlySpendCapCents !== null
          ? String(settingsQuery.data.monthlySpendCapCents / 100)
          : '',
      );
    }
  }, [settingsQuery.data]);

  if (settingsQuery.isLoading) return <div className="p-6 text-muted-foreground">Loading…</div>;
  const current = settingsQuery.data;
  if (!current) return null;

  const save = (): void => {
    const capCents = monthlyCap.trim() === '' ? null : Math.round(parseFloat(monthlyCap) * 100);
    putSettings.mutate({
      version: current.version,
      defaultAutonomyLevel: autonomy,
      monthlySpendCapCents: capCents,
    });
  };

  return (
    <div className="p-6 max-w-2xl mx-auto flex flex-col gap-6">
      <header>
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="text-sm text-muted-foreground">Workspace-wide defaults for campaigns.</p>
      </header>

      <section className="flex flex-col gap-2">
        <span className="text-sm font-medium">Default autonomy for new campaigns</span>
        <div className="flex gap-2">
          {(['manual', 'propose', 'auto'] as const).map((a) => (
            <button
              key={a}
              onClick={() => setAutonomy(a)}
              className={`px-3 py-1.5 rounded-md text-sm border capitalize ${
                autonomy === a
                  ? 'border-primary font-medium'
                  : 'border-border text-muted-foreground'
              }`}
            >
              {a}
            </button>
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-2">
        <label className="text-sm font-medium" htmlFor="adopt-monthly-cap">
          Monthly spend cap across all campaigns (USD, empty = no cap)
        </label>
        <input
          id="adopt-monthly-cap"
          type="number"
          min="0"
          className="w-48 rounded-md border border-border bg-background px-3 py-2 text-sm"
          value={monthlyCap}
          onChange={(e) => setMonthlyCap(e.target.value)}
          placeholder="No cap"
        />
      </section>

      <section className="rounded-lg border border-dashed border-border p-6 text-muted-foreground text-sm">
        Channel connections (Google Ads) and the managed ad-spend wallet appear here when channel
        integration ships.
      </section>

      {putSettings.isError && (
        <div className="text-sm text-rose-600">
          Save failed{' '}
          {putSettings.error instanceof Error && putSettings.error.message.includes('409')
            ? '— settings changed elsewhere; reload and retry'
            : ''}
        </div>
      )}

      <div>
        <button
          onClick={save}
          disabled={putSettings.isPending}
          className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50"
        >
          {putSettings.isPending ? 'Saving…' : 'Save settings'}
        </button>
      </div>
    </div>
  );
}
